import { useRef } from 'react'
import { Field, Form } from 'react-final-form'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'
import styled from 'styled-components'
import { Elements } from '@stripe/react-stripe-js'
import setFieldData from 'final-form-set-field-data'
import { useRegisterMutation, useStartSubscriptionMutation } from '@mwxltd/flix-api-client'
import messages from '@flix/common/domain/membership/messages'
import { Button } from '@flix/common/elements/controls'
import { FormError } from '@flix/common/elements/forms'
import formMsgs from '@flix/common/elements/forms/messages'
import {
  requiredEmail,
  requiredFullName,
  requiredPassword,
} from '@flix/common/elements/forms/validators'
import { Image } from '@flix/common/elements/images/Image/Image'
import { TextInput } from '@flix/common/elements/input/TextInput/TextInput'
import { ActivityIndicator } from '@flix/common/elements/status'
import { Paragraph, Text } from '@flix/common/elements/typography'
import { stripeElementFonts } from '@flix/common/style/theme'
import { useAppState } from '../../../../containers/app/AppState/AppState'
import { PayPalFakeButton } from '../../../../containers/paypal/PayPalFakeButton'
import { PayPalSubscriptionButton } from '../../../../containers/paypal/PayPalSubscriptionButton'
import { StripeCardInput } from '../../../../containers/stripe/components/StripeCardInput'
import { useStripeCardSetupSubmission } from '../../../../containers/stripe/useStripeCardSetupSubmission'
import { useStripeClient } from '../../../../containers/stripe/useStripeClient'

const PAYPAL = 'payPal'

const FormSectionLabel = styled(View)`
  margin-bottom: ${(props) => props.theme.spacing.medium}px;
  align-items: flex-start;
`

const BillingSection = styled(FormSectionLabel)`
  margin-top: ${(props) => props.theme.spacing.small}px;
  flex-direction: row;
`

const SecurePayment = styled(View)`
  align-items: flex-end;
  flex-grow: 1;
`

const WaitHere = styled(View)`
  flex-direction: row;
  margin-bottom: ${(props) => props.theme.spacing.medium}px;
`

const Spinner = styled(ActivityIndicator)`
  margin-right: ${(props) => props.theme.spacing.medium}px;
`

const WaitText = styled(Text)`
  font-size: ${(props) => props.theme.fontSizes.small}px;
`

const OrText = styled(Paragraph)`
  margin-top: ${(props) => props.theme.spacing.smedium}px;
  margin-bottom: ${(props) => props.theme.spacing.larger}px;
  font-weight: ${(props) => props.theme.fontWeights.bold};
  text-align: center;
`

const PleaseWait = () => (
  <WaitHere>
    <Spinner />
    <WaitText>
      <FormattedMessage {...messages.doNotRefresh} />
    </WaitText>
  </WaitHere>
)

const RegisterAndSubscribeForm = ({ authStatus, chosenPlan, marketingSource, onPayPalSubmit }) => {
  // Keep track of whether the user was already registered when reaching this page
  const existingUserRef = useRef(authStatus)
  const { utmTracking, recommendationId } = useAppState()

  const [register] = useRegisterMutation()
  const [startSubscription] = useStartSubscriptionMutation(chosenPlan)

  const { handleCardSubmit } = useStripeCardSetupSubmission({
    onSubmit: (card) => startSubscription('stripe-intent', { ...card, sku: chosenPlan.sku }),
  })

  const onSubmit = (values) =>
    (authStatus.hasSignedIn
      ? Promise.resolve()
      : register({
          ...values,
          ...utmTracking,
          recommendationId,
          fullName: values.name,
        })
    ).then((maybeErrors) => {
      if (maybeErrors) {
        return maybeErrors // If not empty this contains registration final-form errors
      }
      if (values.payPal) {
        return onPayPalSubmit()
      }
      if (!requiredFullName(values.name)) {
        return handleCardSubmit({
          name: values.name,
        })
      } else {
        return { name: 'Card holder name is required' }
      }
    })

  const passwordField = useRef(null)
  const nameField = useRef(null)
  const cardField = useRef(null)

  return (
    <Form onSubmit={onSubmit} mutators={{ setFieldData }} initialValues={{ marketingSource }}>
      {({ handleSubmit, submitting, form }) => {
        const isPayPal = form.getState().values[PAYPAL]
        const submittingPayPal = submitting && !!isPayPal
        const submittingCard = submitting && !isPayPal
        const handleSubmitCard = (ev) => {
          form.change(PAYPAL, false)
          handleSubmit(ev)
        }
        return (
          <form onSubmit={handleSubmitCard} style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Existing users shouldn't see the form, but on payment
                errors after registration it remains visible */}
            {!existingUserRef.current?.hasSignedIn && (
              <>
                <Field
                  name="email"
                  validate={requiredEmail}
                  render={(fieldProps) => (
                    <TextInput
                      {...fieldProps}
                      placeholder={formMsgs.email}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      onSubmitEditing={() => passwordField.current?.focus()}
                      blurOnSubmit={false}
                      returnKeyType="next"
                    />
                  )}
                />
                <Field
                  name="password"
                  validate={requiredPassword}
                  render={(fieldProps) => (
                    <TextInput
                      {...fieldProps}
                      placeholder={formMsgs.password}
                      textContentType="password"
                      autoCapitalize="none"
                      secureTextEntry
                      onSubmitEditing={() => nameField.current?.focus()}
                      blurOnSubmit={false}
                      returnKeyType="next"
                      ref={passwordField}
                    />
                  )}
                />
              </>
            )}

            <BillingSection>
              <Text>
                <FormattedMessage {...messages.enterBillingDetails} />
              </Text>
              <SecurePayment>
                <Image height={24} width={59} publicId={`web/ssl-secure`} />
              </SecurePayment>
            </BillingSection>
            <Field
              name="name"
              render={(fieldProps) => (
                <TextInput
                  {...fieldProps}
                  placeholder={formMsgs.cardHolderName}
                  textContentType="name"
                  autoCapitalize="words"
                  onSubmitEditing={() => cardField.current?.focus()}
                  blurOnSubmit={false}
                  returnKeyType="next"
                  ref={nameField}
                />
              )}
            />
            <Field
              name="card"
              render={(fieldProps) => (
                <StripeCardInput {...fieldProps} onReady={(el) => (cardField.current = el)} />
              )}
            />

            {submittingCard && <PleaseWait />}

            <FormError />

            <Button
              variant="primary"
              disabled={submitting}
              onPress={handleSubmitCard}
              intlMessage={
                submittingCard
                  ? formMsgs.submitting
                  : chosenPlan?.hasFreeTrial
                  ? messages.btnSignUpFree
                  : messages.btnSignUpPaid
              }
            />

            {/* A form requires a type="submit" button to submit on Enter press */}
            <button type="submit" style={{ display: 'none' }} />

            <OrText>
              <FormattedMessage {...messages.orUse} />
            </OrText>

            {submittingPayPal && <PleaseWait />}

            {/*
             * We must look at the pre-existing user, otherwise this button renders just before
             * navigation after registration and again on the next page and the PayPal script
             * load order causes issues.
             */}
            {existingUserRef.current?.hasSignedIn ? (
              <PayPalSubscriptionButton chosenPlan={chosenPlan} />
            ) : (
              <PayPalFakeButton
                disabled={submitting}
                onPress={(ev) => {
                  form.change(PAYPAL, true)
                  handleSubmit(ev)
                }}
              />
            )}
          </form>
        )
      }}
    </Form>
  )
}

// The component rendering the Stripe input must be wrapped in Elements
const withStripeElements = (Component) => (props) => {
  const stripe = useStripeClient()
  return (
    <Elements stripe={stripe} options={{ fonts: stripeElementFonts }}>
      <Component {...props} />
    </Elements>
  )
}

const RegisterAndSubscribeFormWithElements = withStripeElements(RegisterAndSubscribeForm)

export { RegisterAndSubscribeFormWithElements as RegisterAndSubscribeForm }
